import { defineAction } from '~/prix'
import { z } from 'zod'
import expression from '~/prix/types/expression'
import { getZodSchemaInfo } from '~/prix/types/zodType'
import { analysis } from '~/prix/types/presets/analysis'
import { assertions } from '~/prix/types/presets/assertions'
import { select } from '~/prix/types/presets/select'
import { childrenType, spaceForPoint } from '~/prix/types/presets/space'
import { timeForIncidence } from '~/prix/types/presets/time'
import { variable } from '~/prix/types/presets/variable'
import { dateOrIsoString } from '~/prix/types/date'
import { prepareAssertions } from '~/packages/general/variable/prepareAssertions.util'
import { Variable } from '~/packages/general/variable/variables.typing'
import publicHealthAttendanceMedicalProcedureStatsAction from './attendanceMedicalProcedureStats.action.json'
import { attendanceShape } from '../../attendance/stats/attendanceStats.definition'
import { healthVariableBaseRelevance } from '~/packages/general/variable/healthVariableRelevance.util'
import { defaultDetectionLayers } from '~/utils/server/stats/defaultTimeSeriesLayers'


const sigtapShape = z.object({
  code: z.string().describe('Código SIGTAP'),
  description: z.string().describe('Descrição do procedimento'),
  male: z.boolean().describe('Masculino'),
  female: z.boolean().describe('Feminino'),
  outpatientService: z.number().describe('Valor ambulatorial'),
  hospitalService: z.number().describe('Valor hospitalar'),
  professionalService: z.number().describe('Valor profissional'),
  maximumAge: z.string().describe('Idade máxima'),
  minimumAge: z.string().describe('Idade mínima'),
})

export const attendanceMedicalProcedureShape = z.object({
  originalId: z.string().describe('ID original'),
  dataSourceId: z.number().describe('ID da fonte de dados'),
  attendanceOriginalId: z.string().describe('ID original do atendimento'),
  sigtapCode: z.string().describe('Código SIGTAP do procedimento'),
  procedureName: z.string().describe('Nome do procedimento'),
  ...sigtapShape.shape,
  ...attendanceShape.omit({ originalId: true, dataSourceId: true }).shape,
})

const attendanceMedicalProcedureExpression = expression(attendanceMedicalProcedureShape.shape)

export const attendanceMedicalProcedureVariable: Variable = {
  key: 'attendanceMedicalProcedure',
  categoryKey: 'healthProcedures',
  actionDefinitionKey: publicHealthAttendanceMedicalProcedureStatsAction.key,
  title: 'Procedimentos médicos em atendimentos',
  unitSingular: 'procedimento',
  unitPlural: 'procedimentos',
  grammaticalGender: 'male',
  timeType: 'incidence',
  shape: getZodSchemaInfo(attendanceMedicalProcedureShape),
  filterableBy: getZodSchemaInfo(
    attendanceMedicalProcedureShape.pick({
      /* CIDADÃO */
      'citizen.age': true,
      'citizen.sex': true,
      'citizen.deceased': true,
      /* SAÚDE */
      'citizenHealth.hasHypertension': true,
      'citizenHealth.hasDiabetes': true,
      'citizenHealth.hasMedicalProfessionalDiagnosisOfMentalConditions': true,
      'citizenHealth.isPregnant': true,
      'citizenHealth.smoker': true,
      'citizenHealth.skinColorOrRace': true,
      'citizenHealth.educationLevel': true,
      'citizenHealth.employmentStatus': true,
      'citizenHealth.homeless': true,
      'citizenHealth.hasPrivateHealthInsurance': true,
      'citizenHealth.nationality': true,
      /* ATENDIMENTO */
      'typeOfAttendance': true,
      'isVaccinationUpToDate': true,
      'bmi': true,
      /* PROCEDIMENTO */
      'sigtapCode': true,
    }),
  ),
  betterDensity: 'less',
  relevance: healthVariableBaseRelevance.attendanceMedicalProcedure,
  basicSelect: ['citizen.name', 'citizen.socialName'],
  detailedSelect: [
    /* ATENDIMENTO */
    'attendanceDateTime',
    'professionalName',
    'healthUnitName',
    'teamName',
    'weight',
    'height',
    'typeOfAttendance',
    'isVaccinationUpToDate',
    'lastMenstruationDate',

    /* PROCEDIMENTO */
    'procedureName',
    'description',
    'sigtapCode',

    /* CIDADÃO */
    'citizen.sex',
    'citizen.birthDate',
    'citizen.phoneNumber',
    'citizen.cns',
    'citizen.cpf',
    'citizen.streetName',
    'citizen.streetNumber',
    'citizen.neighborhood',
    'citizen.complement',
    'citizen.referencePoint',
    'citizen.postalCode',
    'citizen.deceased',
    'citizen.latitude',
    'citizen.longitude',
    'citizen.originalId',

    /* SAÚDE */
    'citizenHealth.email',
    'citizenHealth.isResponsibleFamilyMember',
    'citizenHealth.hasDiabetes',
    'citizenHealth.hasHypertension',
    'citizenHealth.hasMedicalProfessionalDiagnosisOfMentalConditions',
    'citizenHealth.isPregnant',
    'citizenHealth.smoker',
    'citizenHealth.skinColorOrRace',
    'citizenHealth.educationLevel',
    'citizenHealth.employmentStatus',
    'citizenHealth.homeless',
    'citizenHealth.hasPrivateHealthInsurance',
    'citizenHealth.nationality',
  ],
  timeSeries: {
    model: 'additive',
    fitDays: 365 * 10,
    layers: [
      ...defaultDetectionLayers,
      {
        layer: 'trend',
        degree: 1,
      },
      {
        layer: 'calendarSeasonality',
        type: 'dayOfWeek',
        degree: 0,
      },
      {
        layer: 'calendarSeasonality',
        type: 'monthOfYear',
        degree: 0,
      },
      {
        layer: 'calendarSeasonality',
        type: 'holiday',
        degree: 0,
      },
      {
        layer: 'calendarSeasonality',
        type: 'dayOfMonth',
        degree: 0,
      },
      {
        layer: 'harmonicSeasonality',
        optimization: {
          minimumDays: 30,
          lossFunction: 'gradientMeanSquaredError',
        },
      },
      {
        layer: 'harmonicSeasonality',
        optimization: {
          minimumDays: 30,
          lossFunction: 'gradientMeanSquaredError',
        },
      },
    ],
  },
  assertions: prepareAssertions(attendanceMedicalProcedureExpression),
}

const attendanceMedicalProcedureStatsDefinition = defineAction({
  ...publicHealthAttendanceMedicalProcedureStatsAction,
  input: z.object({
    variable,
    highlight: attendanceMedicalProcedureExpression.optional(),
    filter: attendanceMedicalProcedureExpression.optional(),
    space: spaceForPoint().optional(),
    time: timeForIncidence().optional(),
    select: select(attendanceMedicalProcedureShape),
    assertions,
    analysis,
  }),
  output: z.any(),
  cache: input => {
    if (
      input.filter ||
      input.space?.delimitationType === 'custom' ||
      (input.time?.type &&
        ['customDateRange', 'customDateRangePredicted'].includes(input.time?.type))
    ) {
      return {}
    }
    return {
      back: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
      front: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
    }
  },
})

export default attendanceMedicalProcedureStatsDefinition
